import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Snackbar,
  Alert,
} from "@mui/material";
import { fetchLogs } from "../../api/apiService";
import { format } from "date-fns";
import Navbar from "../../components/Navbar";

function Auditoria() {
  const [startDate, setStartDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [logs, setLogs] = useState([]);
  const [filteredLogs, setFilteredLogs] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const getLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const formattedStartDate = format(
        new Date(startDate + "T00:00:00"),
        "dd/MM/yyyy"
      );
      const formattedEndDate = format(
        new Date(endDate + "T23:59:59"),
        "dd/MM/yyyy"
      );
  
      const data = await fetchLogs(formattedStartDate, formattedEndDate);
  
      const sortedLogs = (Array.isArray(data) ? data : []).sort((a, b) => {
        const dateA = new Date(
          a.timestamp.split("/").reverse().join("-")
        );
        const dateB = new Date(
          b.timestamp.split("/").reverse().join("-")
        );
  
        return dateB - dateA;
      });
  
      setLogs(sortedLogs);
      setFilteredLogs(sortedLogs);
    } catch (err) {
      setError("Erro ao carregar os logs. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  const downloadLogs = async () => {
    try {
      const formattedStartDate = format(new Date(startDate), "dd/MM/yyyy");
      const formattedEndDate = format(new Date(endDate), "dd/MM/yyyy");
      const data = await fetchLogs(formattedStartDate, formattedEndDate, true);

      const blob = new Blob([data], { type: "application/json" });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.download = `logs-${formattedStartDate.replace(
        /\//g,
        "-"
      )}-to-${formattedEndDate.replace(/\//g, "-")}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      setSnackbarMessage("Erro ao baixar os logs. Tente novamente.");
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    const term = e.target.value.toLowerCase();
    const filtered = logs.filter(
      (log) =>
        log.user.toLowerCase().includes(term) ||
        log.action.toLowerCase().includes(term) ||
        log.resource.toLowerCase().includes(term)
    );
    setFilteredLogs(filtered);
  };

  useEffect(() => {
    getLogs();
  }, [getLogs]);

  const handleCloseSnackbar = () => setSnackbarMessage("");

  return (
<Box sx={{ padding: "2rem", marginTop: "20px" }}>
      <Navbar />
      <Box sx={{ padding: "2rem" }}>
        <Typography variant="h4" textAlign="center" mb={3}>
          Auditoria
        </Typography>
        <Typography variant="body1" textAlign="center" mb={4}>
          Veja os logs de auditoria, filtre por usuário ou ação, e baixe os registros.
        </Typography>
        <Box
          display="flex"
          gap={2}
          flexWrap="wrap"
          justifyContent="center"
          mb={3}
        >
          <TextField
            label="Data Início"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Data Fim"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Button variant="contained" color="primary" onClick={getLogs}>
            Buscar Logs
          </Button>
        </Box>
        <TextField
          fullWidth
          placeholder="Pesquisar por usuário, ação ou recurso"
          value={searchTerm}
          onChange={handleSearch}
          sx={{ mb: 4 }}
        />
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" textAlign="center">
            {error}
          </Typography>
        ) : (
          <Grid container spacing={2} justifyContent="center" >
            {filteredLogs.length > 0 ? (
              filteredLogs.map((log, index) => (
                <Grid item xs={12} md={6} lg={4} key={index}>
                  <Card elevation={3}>
                    <CardContent>
                      <Typography variant="body1">
                        <strong>Data:</strong> {log.timestamp}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Usuário:</strong> {log.user}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Ação:</strong> {log.action}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Recurso:</strong> {log.resource}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            ) : (
              <Typography textAlign="center" width="100%">
                Nenhum log encontrado para o termo pesquisado.
              </Typography>
            )}
          </Grid>
        )}
        <Box textAlign="center" mt={4}>
          <Button
            variant="contained"
            color="secondary"
            onClick={downloadLogs}
            disabled={loading || logs.length === 0}
          >
            Baixar Logs
          </Button>
        </Box>
      </Box>
      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity="info"
          sx={{ width: "100%", textAlign: "center" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Auditoria;
